import type { AssetSummary } from '@protoss/shared';
import * as React from 'react';
import { customersApi } from '../services/customersApi';

const ASSET_URL_CACHE_TTL_MS = 5 * 60 * 1000;
const ASSET_URL_CACHE_MAX_ENTRIES = 250;

interface CachedAssetUrl {
  objectUrl: string;
  expiresAt: number;
  lastAccessedAt: number;
}

interface InflightAssetRequest {
  promise: Promise<string>;
  controller: AbortController;
  consumers: number;
}

const resolvedAssetUrlCache = new Map<string, CachedAssetUrl>();
const inflightAssetRequests = new Map<string, InflightAssetRequest>();

function revokeCachedObjectUrl(entry: CachedAssetUrl): void {
  URL.revokeObjectURL(entry.objectUrl);
}

function pruneExpiredCache(now: number): void {
  for (const [key, entry] of resolvedAssetUrlCache.entries()) {
    if (entry.expiresAt <= now) {
      revokeCachedObjectUrl(entry);
      resolvedAssetUrlCache.delete(key);
    }
  }
}

function pruneCacheSize(): void {
  if (resolvedAssetUrlCache.size <= ASSET_URL_CACHE_MAX_ENTRIES) return;

  const overflow = resolvedAssetUrlCache.size - ASSET_URL_CACHE_MAX_ENTRIES;
  const oldest = [...resolvedAssetUrlCache.entries()]
    .sort((a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt)
    .slice(0, overflow);

  for (const [key, entry] of oldest) {
    revokeCachedObjectUrl(entry);
    resolvedAssetUrlCache.delete(key);
  }
}

/**
 * Side-effect-free read of the cache. Safe to call during render.
 * Does not prune or update lastAccessedAt.
 */
function peekCachedObjectUrl(cacheKey: string): string | null {
  const now = Date.now();
  const cached = resolvedAssetUrlCache.get(cacheKey);
  if (!cached || cached.expiresAt <= now) return null;
  return cached.objectUrl;
}

function getCachedObjectUrl(cacheKey: string): string | null {
  const now = Date.now();
  pruneExpiredCache(now);

  const cached = resolvedAssetUrlCache.get(cacheKey);
  if (!cached) return null;

  cached.lastAccessedAt = now;
  return cached.objectUrl;
}

function setCachedObjectUrl(cacheKey: string, objectUrl: string): void {
  const now = Date.now();
  resolvedAssetUrlCache.set(cacheKey, {
    objectUrl,
    expiresAt: now + ASSET_URL_CACHE_TTL_MS,
    lastAccessedAt: now,
  });

  pruneExpiredCache(now);
  pruneCacheSize();
}

function acquireInflightRequest(cacheKey: string): InflightAssetRequest {
  const existing = inflightAssetRequests.get(cacheKey);
  if (existing) {
    existing.consumers += 1;
    return existing;
  }

  const controller = new AbortController();
  const promise = customersApi
    .getProtectedAssetObjectUrl(cacheKey, { signal: controller.signal })
    .then((objectUrl) => {
      setCachedObjectUrl(cacheKey, objectUrl);
      return objectUrl;
    })
    .finally(() => {
      inflightAssetRequests.delete(cacheKey);
    });

  const created: InflightAssetRequest = {
    promise,
    controller,
    consumers: 1,
  };

  inflightAssetRequests.set(cacheKey, created);
  return created;
}

function releaseInflightRequest(cacheKey: string): void {
  const entry = inflightAssetRequests.get(cacheKey);
  if (!entry) return;

  entry.consumers -= 1;
  if (entry.consumers <= 0) {
    entry.controller.abort();
    inflightAssetRequests.delete(cacheKey);
  }
}

/**
 * Resolves an authenticated backend asset endpoint to a local object URL that
 * can be used safely in <img> / Avatar components.
 */
export function useProtectedAssetUrl(asset: AssetSummary | null | undefined): string | null {
  // Lazy initializer: reads module-level cache synchronously so the very first
  // render already has the image URL on in-session cache hits - no initials flash.
  const [src, setSrc] = React.useState<string | null>(() => {
    if (!asset?.contentUrl) return null;
    return peekCachedObjectUrl(asset.contentUrl);
  });

  React.useEffect(() => {
    if (!asset?.contentUrl) {
      setSrc(null);
      return;
    }

    const cacheKey = asset.contentUrl;
    const cachedObjectUrl = getCachedObjectUrl(cacheKey);
    if (cachedObjectUrl) {
      setSrc(cachedObjectUrl);
      return;
    }

    let disposed = false;
    setSrc(null);

    const inflight = acquireInflightRequest(cacheKey);

    inflight.promise
      .then((resolvedUrl) => {
        if (disposed) return;
        setSrc(resolvedUrl);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (!disposed) {
          setSrc(null);
        }
      });

    return () => {
      disposed = true;
      releaseInflightRequest(cacheKey);
    };
  }, [asset?.contentUrl]);

  return src;
}
